import FbButton from 'components/FbUI/FbButton'
import Checkbox from 'components/Checkbox/checkbox'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from 'components/UI/BaseDropdown/dropdown-base'
import { cn } from 'components/UI/cn'
import { useMemo, useRef, useState } from 'react'
import {
  FILTER_POPOVER_BODY,
  FILTER_POPOVER_CONTENT_BASE,
  FILTER_POPOVER_FOOTER,
} from './popoverClasses'
import { FloatingTooltip } from 'components/Tooltip/FloatingTooltip'
import { FilterButton } from '../components/FilterButton'
import { FilterCloseButton } from '../components/FilterCloseButton'
import { FilterSearchInput } from '../components/FilterSearchInput'

/**
 * Store-free sibling of {@link SelectFilter}.
 *
 * `SelectFilter` is the canonical data-table multi-select, but it reads and
 * writes its state through a Zustand `FilterStore` (URL + localStorage synced).
 * This variant renders the identical DS popover — `FilterButton` trigger with a
 * count bubble, "Select all"/Reset header, search, checkbox list, Cancel/Apply
 * footer — but is driven by a plain `selected: string[]` + `onApply` pair so it
 * can drop into surfaces that own their own local filter state (e.g. the
 * campaign replies inbox) without taking on the table FilterStore machinery.
 *
 * No `useEffect` (per client/CLAUDE.md): the in-popover draft is seeded from the
 * applied `selected` in the open handler, committed on Apply / outside-click /
 * Escape, and discarded on Cancel.
 */

export interface InlineSelectFilterOption {
  value: string
  label: string
}

export interface InlineSelectFilterProps {
  /** Trigger label, e.g. "Sender". */
  title: string
  options: InlineSelectFilterOption[]
  /** Applied (committed) values, owned by the parent. */
  selected: string[]
  /** Fired with the new selection on Apply / outside-click / Escape. */
  onApply: (values: string[]) => void
  hideSearch?: boolean
  /** Hides the "Select all" / Reset header (and its divider). Use for short
   *  option sets (≤2) where bulk-select adds no value over toggling each — the
   *  inbox Status/Attachment filters drop it for the same reason. */
  hideBulkHeader?: boolean
  searchPlaceholder?: string
  /** Menu alignment relative to the trigger. Use `end` for right-edge triggers
   *  so the popover opens flush under them instead of being shifted by
   *  collision detection. Defaults to `start`. */
  align?: 'start' | 'center' | 'end'
  /** Opt-in DS2026 secondary trigger: search-bar height (h-9) and a steady
   *  neutral-100 hover fill (vs the shared trigger's border-only hover). Layered
   *  over `filterTriggerClasses` per-instance so other callers are unaffected. */
  ds2026?: boolean
  /** Trigger height for the `ds2026` variant. `md` (default) is h-9 (matches
   *  the DS2026 search pill); `sm` is h-8 so it lines up with `size="sm"`
   *  PillTabs/FbDropdown in a dense filter row. No-op without `ds2026`. */
  size?: 'sm' | 'md'
}

export function InlineSelectFilter({
  title,
  options,
  selected,
  onApply,
  hideSearch = false,
  hideBulkHeader = false,
  searchPlaceholder = 'Search',
  align = 'start',
  ds2026 = false,
  size = 'md',
}: InlineSelectFilterProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [searchFocused, setSearchFocused] = useState(false)
  // In-popover working copy of the selection; committed on Apply / close.
  const [draft, setDraft] = useState<string[]>(selected)
  // Snapshot of the applied selection at open time. Drives the "selected to the
  // top" sort only, so toggling a checkbox mid-edit doesn't reflow the list and
  // make the item appear to jump away under the cursor.
  const [frozen, setFrozen] = useState<Set<string>>(() => new Set(selected))
  // Cancel raises this so the controlled close doesn't auto-commit; outside
  // click / Escape still commit (mirrors SelectFilter's behavior).
  const skipNextSaveRef = useRef(false)

  const isActive = selected.length > 0
  // DS2026 secondary trigger override (opt-in). Search-bar height (h-9) and a
  // flat gray secondary across resting/hover/open/active — overriding the shared
  // trigger's border-only hover, green focus ring, and basil "active" fill so
  // these read as plain gray secondary buttons (matching the inbox filters).
  const ds2026TriggerClass = ds2026
    ? cn(
        // sm matches the FbDropdown neutral trigger (h-8 + rounded-md); md is
        // the unchanged default (keeps the shared pill's rounded-lg).
        size === 'sm' ? '!h-8 !min-h-0 !rounded-md' : '!h-9 !min-h-0',
        '!bg-ds2026-btn-outline-bg !border-ds2026-btn-outline-stroke !text-ds2026-btn-outline-text',
        'hover:!bg-ds2026-btn-outline-hover-bg hover:!border-ds2026-btn-outline-stroke hover:!text-ds2026-btn-outline-hover-text',
        open && '!bg-ds2026-btn-outline-hover-bg !ring-0'
      )
    : undefined

  // DS2026 inline summary: the trigger shows "Title: <first> +N" (vs the default
  // title + count bubble), with the full list surfaced in a hover tooltip.
  const selectedLabels = useMemo(
    () => selected.map((v) => options.find((o) => o.value === v)?.label ?? v),
    [selected, options]
  )
  const triggerTitle =
    ds2026 && isActive
      ? selectedLabels.length > 1
        ? `${title}: ${selectedLabels[0]} +${selectedLabels.length - 1}`
        : `${title}: ${selectedLabels[0]}`
      : title

  const allSelected = options.length > 0 && draft.length === options.length
  const someSelected = draft.length > 0

  // O(1) membership lookup for the option list, so a large option set (e.g.
  // hundreds of campaigns on a future table-style surface) stays linear instead
  // of O(n²) against the `draft` array.
  const draftSet = useMemo(() => new Set(draft), [draft])

  const filteredOptions = useMemo(() => {
    const q = search.trim().toLowerCase()
    return options
      .filter((o) => draftSet.has(o.value) || o.label.toLowerCase().includes(q))
      .sort((a, b) => {
        // Pin-to-top uses the open-time `frozen` snapshot, not the live draft,
        // so checking an item mid-edit doesn't make it jump to the top under
        // the cursor. Visibility, above, uses `draftSet` so a currently-checked
        // item stays in the list (and thus deselectable) regardless of search.
        const aSel = frozen.has(a.value) ? 1 : -1
        const bSel = frozen.has(b.value) ? 1 : -1
        if (aSel !== bSel) return bSel - aSel
        return a.label.localeCompare(b.label)
      })
  }, [options, search, frozen, draftSet])

  function toggle(value: string) {
    setDraft((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    )
  }

  function handleOpenChange(next: boolean) {
    if (next) {
      // Rebuild the draft from the committed selection on every open so a
      // previously-cancelled edit doesn't leak into the next session.
      setDraft(selected)
      setFrozen(new Set(selected))
      setSearch('')
      // Clear any stale cancel flag: Cancel closes via a controlled
      // setOpen(false) that never routes through this handler, so the flag it
      // raised would otherwise persist and silently discard the next applied
      // selection on outside-click / Escape.
      skipNextSaveRef.current = false
    } else if (skipNextSaveRef.current) {
      skipNextSaveRef.current = false
    } else {
      onApply(draft)
    }
    setOpen(next)
  }

  const triggerGroup = (
    <div className="relative inline-flex">
      <DropdownMenuTrigger asChild>
        <FilterButton
          open={open}
          title={triggerTitle}
          isActive={isActive}
          count={ds2026 ? undefined : selected.length}
          className={ds2026TriggerClass}
        />
      </DropdownMenuTrigger>
      <FilterCloseButton
        isActive={isActive}
        open={open}
        onClick={() => onApply([])}
      />
    </div>
  )

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      {ds2026 && selectedLabels.length > 1 ? (
        <FloatingTooltip
          theme="dark"
          text={
            <div className="flex flex-col gap-0.5">
              {selected.map((v, i) => (
                <span key={v}>{selectedLabels[i]}</span>
              ))}
            </div>
          }
        >
          {triggerGroup}
        </FloatingTooltip>
      ) : (
        triggerGroup
      )}

      <DropdownMenuContent
        side="bottom"
        align={align}
        sideOffset={6}
        collisionPadding={12}
        className={cn(
          FILTER_POPOVER_CONTENT_BASE,
          // DS2026 filters hug their content (like the inbox), instead of a
          // fixed wide panel that looks oversized for short option lists.
          ds2026
            ? 'min-w-[200px] w-max max-w-[320px]'
            : 'w-[280px] lg:w-[260px]'
        )}
      >
        <div className={FILTER_POPOVER_BODY}>
          {/* Bulk-select header: tri-state checkbox toggles all/none; Reset
              clears the draft. */}
          {options.length > 0 && !hideBulkHeader && (
            <>
              <div className="flex items-center justify-between gap-2 pb-2">
                <div
                  role="checkbox"
                  aria-checked={
                    allSelected ? true : someSelected ? 'mixed' : false
                  }
                  tabIndex={0}
                  className="flex min-w-0 cursor-pointer items-center gap-2"
                  onClick={() =>
                    setDraft(allSelected ? [] : options.map((o) => o.value))
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      setDraft(allSelected ? [] : options.map((o) => o.value))
                    }
                  }}
                >
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected && !allSelected}
                    readOnly
                    tabIndex={-1}
                    aria-hidden
                    wrapperClassName="flex-shrink-0 pointer-events-none"
                  />
                  <span className="truncate text-sm font-semibold text-neutral-800">
                    Select all
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setDraft([])}
                  disabled={!someSelected}
                  className={cn(
                    'text-sm font-medium transition-colors',
                    someSelected
                      ? 'text-brand-active hover:text-brand-bright'
                      : 'cursor-not-allowed text-neutral-400'
                  )}
                >
                  Reset
                </button>
              </div>
              <hr className="-mx-3 mb-2 border-0 border-t border-neutral-200" />
            </>
          )}

          {!hideSearch && (
            <FilterSearchInput
              value={search}
              onChange={setSearch}
              isFocused={searchFocused}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
              placeholder={searchPlaceholder}
            />
          )}

          <div className="mt-2 max-h-[150px] w-full overflow-y-auto overflow-x-hidden">
            {filteredOptions.length === 0 ? (
              <div className="px-2 py-4 text-center text-sm text-neutral-400">
                No matches
              </div>
            ) : (
              filteredOptions.map((o) => {
                const isSelected = draftSet.has(o.value)
                return (
                  <div
                    key={o.value}
                    role="checkbox"
                    aria-checked={isSelected}
                    tabIndex={0}
                    className="flex max-w-full cursor-pointer items-center gap-2 overflow-hidden text-ellipsis whitespace-nowrap rounded-lg px-2 py-2 text-sm text-neutral-700 hover:bg-neutral-100"
                    // The Checkbox has `pointer-events-none`, so a click on the
                    // box visual passes through to this row handler rather than
                    // double-firing.
                    onClick={() => toggle(o.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        toggle(o.value)
                      }
                    }}
                  >
                    <Checkbox
                      checked={isSelected}
                      readOnly
                      tabIndex={-1}
                      aria-hidden
                      wrapperClassName="flex-shrink-0 pointer-events-none"
                    />
                    <div className="overflow-hidden text-ellipsis whitespace-nowrap">
                      {o.label}
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </div>

        <div
          className={cn(
            FILTER_POPOVER_FOOTER,
            'mt-3 items-center justify-end gap-2'
          )}
        >
          <FbButton
            size="sm"
            variant="secondary"
            type="button"
            onClick={() => {
              skipNextSaveRef.current = true
              setOpen(false)
            }}
          >
            Cancel
          </FbButton>
          <FbButton
            size="sm"
            type="button"
            className="!bg-brand-bright !text-white hover:!bg-brand-active active:!bg-brand-active disabled:!border-neutral-100 disabled:!bg-neutral-100 disabled:!text-neutral-400 disabled:hover:!bg-neutral-100"
            // Stays enabled when a filter is already applied so the user can
            // clear the checkboxes and Apply the empty state to drop it.
            disabled={!someSelected && !isActive}
            onClick={() => {
              onApply(draft)
              setOpen(false)
            }}
          >
            Apply
          </FbButton>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
